import { memo } from 'react';
import { type ColorInfo, NEXT_STATUS, type Task, capitalize, daysBetween, daysSince, fmtAgo, fmtRelativeDay, fmtShort, localToday, normalize, plural, statusName } from '@quadro/domain';
import { Avatar } from '../../components/Avatar';
import { Icon } from '../../components/Icon';
import { useWorkspace } from '../../state/workspace';

export function classificationText(t: Task, ws: ReturnType<typeof useWorkspace>) {
  const c = t.classification && ws.settings.categories.find(x => x.id === t.classification!.categoryId);
  if (!c) return '';
  const s = c.subcategories.find(x => x.id === t.classification!.subcategoryId);
  return s ? `${c.name} › ${s.name}` : c.name;
}

interface Props {
  task: Task;
  dim: boolean;
  color: ColorInfo | null;
  showProject: boolean;
  entityName: (id: string) => string;
  onOpen: (id: string) => void;
  onAdvance: (id: string) => void;
  onComplete: (id: string) => void;
}

export const TaskCard = memo(function TaskCard({ task: t, dim, color, showProject, entityName, onOpen, onAdvance, onComplete }: Props) {
  const ws = useWorkspace();
  const today = localToday();
  const done = t.status === 'done';
  const p = t.projectId ? ws.settings.projects.find(x => x.id === t.projectId) : undefined;
  const subsDone = t.subtasks.filter(s => s.done).length;
  const subsLate = t.subtasks.filter(s => !s.done && s.due && s.due < today).length;
  const mySubs = t.assigneeId !== ws.me.id ? t.subtasks.filter(s => !s.done && s.assigneeId === ws.me.id).length : 0;
  const themes = t.themeIds.map(id => ws.settings.themes.find(x => x.id === id)).filter(Boolean) as { id: string; name: string; color: string }[];
  const tax = classificationText(t, ws);
  const next = NEXT_STATUS[t.status];

  const meta: React.ReactNode[] = [];
  if (p && showProject) meta.push(<span className="m-proj" key="p"><i style={{ background: p.color }} />{p.name}</span>);
  if (done) meta.push(<span key="d">{capitalize(fmtAgo(t.completedAt || t.enteredStatusAt))}</span>);
  else {
    if (t.due) {
      const n = daysBetween(today, t.due);
      meta.push(n < 0 ? <span key="due" className="red">Atrasada {plural(-n, 'dia', 'dias')}</span>
        : n <= 1 ? <span key="due" className="strong">{capitalize(fmtRelativeDay(t.due, today))}</span>
        : <span key="due">{fmtShort(t.due)}</span>);
    }
    if (t.status === 'waiting' && t.waiting) {
      const who = t.waiting.who.trim();
      if (who && normalize(who) !== 'terceiros') meta.push(<span key="who" className="strong">{who}</span>);
      if (t.waiting.followUpOn) {
        const n = daysBetween(today, t.waiting.followUpOn);
        meta.push(n < 0 ? <span key="fu" className="red">Cobrança atrasada</span> : n === 0 ? <span key="fu" className="red">Cobrar hoje</span> : <span key="fu">Cobrar {fmtRelativeDay(t.waiting.followUpOn, today)}</span>);
      }
    }
    const d = daysSince(t.enteredStatusAt);
    if (t.status === 'doing' && d >= 5) meta.push(<span key="st" className={d >= 8 ? 'red' : 'orange'} title="Sem mudar de etapa">Parada há {d} dias</span>);
    if (t.status === 'mapped' && d >= 14) meta.push(<span key="st" title="Desde que foi mapeada">Há {d} dias</span>);
  }
  if (t.routineId) meta.push(<span key="r" className="m-rec" title="Criada por uma rotina"><Icon name="rec" />Rotina</span>);
  if (t.links.length) meta.push(<span key="l" className="m-link" title={t.links.map(l => l.title).join('\n')}><Icon name="link" />{t.links.length}</span>);
  if (t.entityIds.length) meta.push(<span key="e" className="m-ent" title={t.entityIds.map(entityName).join(', ')}><Icon name="ent" />{entityName(t.entityIds[0])}{t.entityIds.length > 1 ? ` +${t.entityIds.length - 1}` : ''}</span>);

  const style = color && !color.empty ? ({ '--kc': color.color, '--kt': `${color.tint ?? 0}%` } as React.CSSProperties) : undefined;
  return (
    <article className={`kcard ${done ? 'fin' : ''} ${dim ? 'dim' : ''}`} data-card={t.id} data-cor={style ? '' : undefined} style={style}
      draggable={ws.canWrite} tabIndex={0} aria-label={`${t.title}, ${statusName(t.status)}`} onClick={() => onOpen(t.id)}>
      {tax && <div className="k-tax">{tax}</div>}
      <div className="k-row">
        <div className="k-title" title={t.title}>{t.priority === 3 && !done && <span className="k-alta" title="Prioridade alta">!</span>}{t.title}</div>
        <Avatar id={t.assigneeId} />
      </div>
      {meta.length > 0 && <div className="k-meta">{meta}</div>}
      {themes.length > 0 && (
        <div className="k-temas">
          {themes.slice(0, 3).map(x => <span key={x.id} className="tchip" style={{ '--tc': x.color } as React.CSSProperties}>{x.name}</span>)}
          {themes.length > 3 && <span className="tchip more">+{themes.length - 3}</span>}
        </div>
      )}
      {ws.canWrite && !done && (
        <div className="k-acts" onClick={e => e.stopPropagation()}>
          {next && next !== 'done' && (
            <button type="button" className="k-act" onClick={() => onAdvance(t.id)} title={`Mover para ${statusName(next)}`} aria-label={`Mover para ${statusName(next)}`}>
              <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M2 6h7.5M6.5 2.8L9.7 6 6.5 9.2" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
          )}
          <button type="button" className="k-act ok" onClick={() => onComplete(t.id)} title="Finalizar" aria-label="Finalizar"><Icon name="tick" /></button>
        </div>
      )}
      {t.subtasks.length > 0 && (
        <div className="k-subs" title="Subtarefas">
          <span className="k-bar"><i style={{ width: `${(subsDone / t.subtasks.length) * 100}%` }} /></span>
          <span>{subsDone}/{t.subtasks.length}</span>
          {subsLate > 0 && <span className="red">{plural(subsLate, 'atrasada', 'atrasadas')}</span>}
          {mySubs > 0 && <span className="mine">{plural(mySubs, 'sua', 'suas')}</span>}
        </div>
      )}
    </article>
  );
});
